import type { AxiosError } from 'axios';

/**
 * Normalised error the app works with instead of raw AxiosErrors. Every
 * caller (including React Query) sees the same shape regardless of whether
 * the failure came from the network, the BE, or a 4xx.
 *
 * Gateway v3 envelope (errors):
 * `{ success: false, statusCode, error: { code, message, details? }, requestId? }`
 */

/** Gateway `error.code` values (exact wire strings). */
export const GATEWAY_ERROR_CODES = [
  'VALIDATION_ERROR',
  'UNAUTHORIZED',
  'FORBIDDEN',
  'NOT_FOUND',
  'CONFLICT',
  'RATE_LIMITED',
  'SERVICE_UNAVAILABLE',
  'GATEWAY_TIMEOUT',
  'INTERNAL_ERROR',
  'NETWORK_ERROR',
  'UNKNOWN',
] as const;

export type GatewayErrorCode = (typeof GATEWAY_ERROR_CODES)[number];

export class ApiError extends Error {
  readonly status: number;
  /** Normalised gateway code (UPPER_SNAKE_CASE). */
  readonly code: string;
  /** Field-level validation errors keyed by path (when `details` is structured). */
  readonly fieldErrors?: Record<string, string[]>;
  /**
   * Unstructured multi-message validation / business detail lines from
   * `error.details[]` (strings, or messages extracted from objects).
   */
  readonly details?: string[];
  /** Correlates FE reports with server logs. */
  readonly requestId?: string;
  /** Raw server payload, useful for the error boundary / Sentry. */
  readonly raw?: unknown;

  constructor(init: {
    status: number;
    code: string;
    message: string;
    fieldErrors?: Record<string, string[]>;
    details?: string[];
    requestId?: string;
    raw?: unknown;
  }) {
    super(init.message);
    this.name = 'ApiError';
    this.status = init.status;
    this.code = init.code;
    this.fieldErrors = init.fieldErrors;
    this.details = init.details;
    this.requestId = init.requestId;
    this.raw = init.raw;
  }

  get isUnauthorized(): boolean {
    return this.code === 'UNAUTHORIZED' || this.status === 401;
  }

  get isForbidden(): boolean {
    return this.code === 'FORBIDDEN' || this.status === 403;
  }

  get isNotFound(): boolean {
    return this.code === 'NOT_FOUND' || this.status === 404;
  }

  get isValidation(): boolean {
    return this.code === 'VALIDATION_ERROR' || this.status === 400 || this.status === 422;
  }

  get isConflict(): boolean {
    return this.code === 'CONFLICT' || this.status === 409;
  }

  get isRetryable(): boolean {
    return (
      this.code === 'RATE_LIMITED' ||
      this.code === 'SERVICE_UNAVAILABLE' ||
      this.code === 'GATEWAY_TIMEOUT' ||
      this.status === 429 ||
      this.status === 503 ||
      this.status === 504
    );
  }
}

interface GatewayErrorObject {
  code?: string;
  message?: string;
  details?: unknown;
}

interface ErrorBody {
  code?: string;
  message?: string;
  fieldErrors?: unknown;
  requestId?: string;
  error?: GatewayErrorObject;
  success?: boolean;
  statusCode?: number;
  data?: unknown;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return null;
}

/**
 * Map HTTP status → gateway code when the body omits `error.code`.
 */
export function inferGatewayCode(status: number): GatewayErrorCode {
  if (status === 0) return 'NETWORK_ERROR';
  if (status === 400 || status === 422) return 'VALIDATION_ERROR';
  if (status === 401) return 'UNAUTHORIZED';
  if (status === 403) return 'FORBIDDEN';
  if (status === 404) return 'NOT_FOUND';
  if (status === 409) return 'CONFLICT';
  if (status === 429) return 'RATE_LIMITED';
  if (status === 503) return 'SERVICE_UNAVAILABLE';
  if (status === 504) return 'GATEWAY_TIMEOUT';
  if (status >= 500) return 'INTERNAL_ERROR';
  return 'UNKNOWN';
}

/**
 * Normalise wire codes (and a few legacy FE lowercase values) to gateway form.
 * `TOKEN_EXPIRED` is not emitted by the current JWT guard — treat as UNAUTHORIZED.
 */
export function normalizeGatewayCode(
  raw: string | undefined | null,
  status: number,
): GatewayErrorCode {
  const code = (raw ?? '').trim().toUpperCase();

  if (!code) return inferGatewayCode(status);

  // Defined by some stacks / older FE codes — never emit as a separate UI path.
  if (code === 'TOKEN_EXPIRED' || code === 'UNAUTHENTICATED') return 'UNAUTHORIZED';

  // Legacy snake_case from older FE inferCode values.
  const legacy: Record<string, GatewayErrorCode> = {
    NETWORK: 'NETWORK_ERROR',
    NETWORK_ERROR: 'NETWORK_ERROR',
    UNAUTHENTICATED: 'UNAUTHORIZED',
    UNAUTHORIZED: 'UNAUTHORIZED',
    FORBIDDEN: 'FORBIDDEN',
    NOT_FOUND: 'NOT_FOUND',
    CONFLICT: 'CONFLICT',
    RATE_LIMITED: 'RATE_LIMITED',
    SERVICE_UNAVAILABLE: 'SERVICE_UNAVAILABLE',
    GATEWAY_TIMEOUT: 'GATEWAY_TIMEOUT',
    INTERNAL_ERROR: 'INTERNAL_ERROR',
    SERVER_ERROR: 'INTERNAL_ERROR',
    VALIDATION_ERROR: 'VALIDATION_ERROR',
    HTTP_ERROR: 'UNKNOWN',
    API_ERROR: 'UNKNOWN',
    UNKNOWN: 'UNKNOWN',
  };

  if (code in legacy) return legacy[code]!;

  // Lowercase legacy (e.g. "forbidden")
  const lowerLegacy: Record<string, GatewayErrorCode> = {
    unauthenticated: 'UNAUTHORIZED',
    unauthorized: 'UNAUTHORIZED',
    forbidden: 'FORBIDDEN',
    not_found: 'NOT_FOUND',
    conflict: 'CONFLICT',
    rate_limited: 'RATE_LIMITED',
    server_error: 'INTERNAL_ERROR',
    network: 'NETWORK_ERROR',
    validation_error: 'VALIDATION_ERROR',
  };
  const fromLower = lowerLegacy[(raw ?? '').trim().toLowerCase()];
  if (fromLower) return fromLower;

  // Prefer explicit unknown wire codes as INTERNAL when 5xx, else keep inferred status mapping.
  if ((GATEWAY_ERROR_CODES as readonly string[]).includes(code)) {
    return code as GatewayErrorCode;
  }

  return inferGatewayCode(status);
}

function mergeFieldErrors(
  ...parts: Array<Record<string, string[]> | undefined>
): Record<string, string[]> | undefined {
  const out: Record<string, string[]> = {};
  for (const part of parts) {
    if (!part) continue;
    for (const [key, messages] of Object.entries(part)) {
      if (!messages?.length) continue;
      out[key] = [...(out[key] ?? []), ...messages];
    }
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

/** Parse classic `fieldErrors: { email: ["…"] | "…" }` shapes. */
function parseFieldErrorsObject(raw: unknown): Record<string, string[]> | undefined {
  const o = asRecord(raw);
  if (!o) return undefined;
  const out: Record<string, string[]> = {};
  for (const [key, val] of Object.entries(o)) {
    if (typeof val === 'string' && val.trim()) {
      out[key] = [val.trim()];
    } else if (Array.isArray(val)) {
      const msgs = val.filter((x): x is string => typeof x === 'string' && x.trim().length > 0);
      if (msgs.length) out[key] = msgs;
    }
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

/**
 * `error.details` may be string[], or objects with field/message paths
 * (common Nest / gateway validation shapes).
 */
export function parseErrorDetails(details: unknown): {
  details: string[];
  fieldErrors?: Record<string, string[]>;
} {
  if (!Array.isArray(details) || details.length === 0) {
    return { details: [] };
  }

  const lines: string[] = [];
  const fieldErrors: Record<string, string[]> = {};

  for (const item of details) {
    if (typeof item === 'string' && item.trim()) {
      lines.push(item.trim());
      continue;
    }
    const o = asRecord(item);
    if (!o) continue;

    const fieldRaw = o.field ?? o.path ?? o.property ?? o.name;
    const messageRaw = o.message ?? o.msg ?? o.error ?? o.description;
    const field = typeof fieldRaw === 'string' ? fieldRaw.trim() : '';
    const message = typeof messageRaw === 'string' ? messageRaw.trim() : '';

    if (field && message) {
      fieldErrors[field] = [...(fieldErrors[field] ?? []), message];
      lines.push(`${field}: ${message}`);
    } else if (message) {
      lines.push(message);
    } else if (field) {
      lines.push(field);
    }
  }

  return {
    details: lines,
    fieldErrors: Object.keys(fieldErrors).length > 0 ? fieldErrors : undefined,
  };
}

export interface ParsedGatewayError {
  status: number;
  code: GatewayErrorCode;
  message: string;
  details?: string[];
  fieldErrors?: Record<string, string[]>;
  requestId?: string;
  raw?: unknown;
}

/**
 * Parse a gateway envelope / error JSON body into a stable shape.
 * Used by HTTP interceptors and envelope unwrap.
 */
export function parseGatewayErrorBody(
  body: unknown,
  httpStatus: number,
  fallbackMessage = 'Request failed',
): ParsedGatewayError {
  const b = asRecord(body) as ErrorBody | null;
  if (!b) {
    return {
      status: httpStatus,
      code: inferGatewayCode(httpStatus),
      message: fallbackMessage,
      raw: body,
    };
  }

  const nested = b.error && typeof b.error === 'object' ? b.error : undefined;
  const status =
    typeof b.statusCode === 'number' && b.statusCode > 0
      ? b.statusCode
      : httpStatus > 0
        ? httpStatus
        : 0;

  const rawCode = nested?.code ?? b.code;
  const code = normalizeGatewayCode(
    typeof rawCode === 'string' ? rawCode : undefined,
    status,
  );

  const message =
    (typeof nested?.message === 'string' && nested.message.trim().length > 0
      ? nested.message.trim()
      : null) ??
    (typeof b.message === 'string' && b.message.trim().length > 0 ? b.message.trim() : null) ??
    fallbackMessage;

  const fromDetails = parseErrorDetails(nested?.details);
  // Some gateways put details at the top level.
  const topDetails = parseErrorDetails((b as { details?: unknown }).details);
  const detailLines = [...fromDetails.details, ...topDetails.details];
  const uniqueDetails = Array.from(new Set(detailLines));

  const fieldErrors = mergeFieldErrors(
    fromDetails.fieldErrors,
    topDetails.fieldErrors,
    parseFieldErrorsObject(b.fieldErrors),
  );

  const requestId =
    typeof b.requestId === 'string' && b.requestId.trim().length > 0
      ? b.requestId.trim()
      : undefined;

  return {
    status,
    code,
    message,
    details: uniqueDetails.length > 0 ? uniqueDetails : undefined,
    fieldErrors,
    requestId,
    raw: body,
  };
}

export function toApiError(err: unknown): ApiError {
  if (err instanceof ApiError) return err;

  const ax = err as AxiosError<ErrorBody>;
  if (ax && typeof ax === 'object' && ax.isAxiosError) {
    const httpStatus = ax.response?.status ?? 0;
    const parsed = parseGatewayErrorBody(
      ax.response?.data,
      httpStatus,
      ax.message || 'Request failed',
    );
    // Prefer response status when body statusCode is missing/zero
    const status = httpStatus > 0 ? httpStatus : parsed.status;
    return new ApiError({
      status,
      code: normalizeGatewayCode(parsed.code, status),
      message: parsed.message,
      fieldErrors: parsed.fieldErrors,
      details: parsed.details,
      requestId: parsed.requestId,
      raw: parsed.raw ?? ax.response?.data,
    });
  }

  if (err instanceof Error) {
    return new ApiError({
      status: 0,
      code: 'NETWORK_ERROR',
      message: err.message || 'Unknown error',
      raw: err,
    });
  }

  return new ApiError({
    status: 0,
    code: 'UNKNOWN',
    message: 'Unknown error',
    raw: err,
  });
}

/**
 * Build an {@link ApiError} from a failed gateway envelope when HTTP status was 2xx
 * but `success: false`, or when unwrap fails.
 */
export function apiErrorFromEnvelope(body: unknown, httpStatus: number): ApiError {
  const parsed = parseGatewayErrorBody(body, httpStatus, 'Request failed');
  return new ApiError({
    status: parsed.status || httpStatus || 0,
    code: parsed.code,
    message: parsed.message,
    fieldErrors: parsed.fieldErrors,
    details: parsed.details,
    requestId: parsed.requestId,
    raw: parsed.raw ?? body,
  });
}
